/**
 * ConversationMediaSection - Seção de mídias e documentos no sidebar do contato
 * Exibe preview de até 3 anexos com contador e botão "Ver tudo"
 * 
 * Versão: 2.0.0 - Refatorado para usar hook centralizado com cache compartilhado
 */

import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Paperclip, ChevronRight } from 'lucide-react';
import { MediaThumbnail } from './MediaThumbnail';
import { ConversationMediaGallery } from './ConversationMediaGallery';
import { useConversationAttachments } from '@/hooks/useConversationAttachments';

interface ConversationMediaSectionProps {
  conversationId: string;
}

export function ConversationMediaSection({ conversationId }: ConversationMediaSectionProps) {
  const [showGallery, setShowGallery] = useState(false);

  // Hook centralizado - busca apenas COUNT + 3 previews (otimizado)
  const {
    previewAttachments,
    totalCount,
    isLoadingPreview,
    // Dados para a galeria (compartilhados via cache)
    galleryAttachments,
    isLoadingGallery,
    isFetchingMore,
    hasMorePages,
    fetchNextPage,
    loadGallery,
  } = useConversationAttachments({ conversationId });

  // Handler para abrir galeria - inicia carregamento sob demanda
  const handleOpenGallery = () => {
    loadGallery();
    setShowGallery(true);
  };

  // Não renderiza se não houver anexos (após carregamento)
  if (!isLoadingPreview && totalCount === 0) {
    return null;
  }

  return (
    <>
      <div className="space-y-3">
        {/* Header com título e contador */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Paperclip className="h-4 w-4 text-muted-foreground" />
            <h5 className="text-sm font-medium text-muted-foreground uppercase tracking-wider">
              Mídias e documentos
            </h5>
          </div>
          {!isLoadingPreview && totalCount > 0 && (
            <span className="text-xs text-muted-foreground">
              {totalCount} {totalCount === 1 ? 'arquivo' : 'arquivos'}
            </span>
          )}
        </div>

        {/* Grid de thumbnails ou skeleton */}
        {isLoadingPreview ? (
          <div className="flex gap-2">
            <Skeleton className="w-16 h-16 rounded-lg" />
            <Skeleton className="w-16 h-16 rounded-lg" />
            <Skeleton className="w-16 h-16 rounded-lg" />
          </div>
        ) : (
          <div className="flex gap-2 flex-wrap">
            {previewAttachments.map((attachment) => (
              <MediaThumbnail
                key={attachment.id}
                type={attachment.type}
                url={attachment.url}
                thumbnailUrl={attachment.thumbnail_url}
                filename={attachment.filename}
                mimeType={attachment.mime_type}
                size={64}
                onClick={handleOpenGallery}
              />
            ))}
          </div>
        )}

        {/* Botão "Ver tudo" se houver mais de 3 */}
        {!isLoadingPreview && totalCount > 3 && (
          <Button
            variant="ghost"
            size="sm"
            className="w-full justify-between text-muted-foreground hover:text-foreground"
            onClick={handleOpenGallery}
          >
            <span>Ver tudo ({totalCount})</span>
            <ChevronRight className="h-4 w-4" />
          </Button>
        )}
      </div>

      {/* Modal de galeria - recebe dados do hook compartilhado */}
      <ConversationMediaGallery
        open={showGallery}
        onOpenChange={setShowGallery}
        attachments={galleryAttachments}
        totalCount={totalCount}
        isLoading={isLoadingGallery}
        isFetchingMore={isFetchingMore}
        hasMorePages={hasMorePages}
        onLoadMore={fetchNextPage}
      />
    </>
  );
}
